import { servicePost } from "./client.ts";
import type { ReturnObject } from "../types/returnObject.ts";

/* ── Types ─────────────────────────────────────────────── */

export interface Promotion {
  PromotionID: number;
  Title: string;
  Description: string;
  PurchaseType: number;
  ValidTimes: number;
  ValidTimesFor: number;
  ValidOnKiosk: number;
  ValidDays: string;
  DiscountType: number;
  Discount: number;
  DiscountFor: number;
  DiscountMinCart: number;
  DiscountMaxCart: number;
  ImageFilename: string;
  Status: string;
  Visibility: number;
  ExtendedDays: number;
  PromotionListID: number;
  ApplyDiscountToTax: boolean;
  PromotionLocationListID: number;
  CalcTS: number;
  OperatorID: number;
  AdminPromotion: boolean;
}

export interface PromotionList {
  iPromotionListID: number;
  sListName: string;
  sType: string;
}

/**
 * "-- Select --" sentinel for PurchaseType. Real DB values are 0-based (see
 * searchEditPromotionPage.tsx getAppliesToOptions); 0 is a valid option
 * ("Rentals"/"Any size"), so it can't double as the unset value. Never sent to
 * the server: SavePromotionInfo rejects it with an empty result and no error.
 * Modules without an "Applies to" picker get 0 instead (normalizePromotion).
 */
export const PURCHASE_TYPE_UNSET = -1;

export function emptyPromotion(): Promotion {
  return {
    PromotionID: 0,
    Title: "",
    Description: "",
    PurchaseType: PURCHASE_TYPE_UNSET,
    ValidTimes: 0,
    ValidTimesFor: 0,
    ValidOnKiosk: 0,
    ValidDays: "0,1,2,3,4,5,6",
    DiscountType: 0,
    Discount: 0,
    DiscountFor: 0,
    DiscountMinCart: 0,
    DiscountMaxCart: -1,
    ImageFilename: "",
    Status: "",
    Visibility: 0,
    ExtendedDays: 0,
    PromotionListID: 0,
    ApplyDiscountToTax: false,
    PromotionLocationListID: 0,
    CalcTS: 0,
    OperatorID: 0,
    AdminPromotion: false,
  };
}

/* ── API ───────────────────────────────────────────────── */

export function getPromotionInfo(promotionId: number): Promise<ReturnObject<Promotion>> {
  return servicePost<Promotion>("GetPromotionInfo", { promotionId });
}

export function savePromotionInfo(promotion: Promotion): Promise<ReturnObject<Promotion>> {
  return servicePost<Promotion>("SavePromotionInfo", { promotion });
}

export function getPromotionLists(): Promise<ReturnObject<PromotionList[]>> {
  return servicePost<PromotionList[]>("WebGetPromotionLists", { promoID: 0 });
}

/* ── Promotion list item management ───────────────────── */

export interface PromotionItemList {
  id: number;
  name?: string;
  type: string;
  items: number[];
}

export function getItemList(id: number, type: string): Promise<ReturnObject<PromotionItemList>> {
  return servicePost<PromotionItemList>("WebGetItemList", { id, type });
}

export function saveItemList(item: PromotionItemList): Promise<ReturnObject<PromotionItemList>> {
  return servicePost<PromotionItemList>("WebSaveItemList", { item });
}

export function createProductList(name: string, sType: string): Promise<ReturnObject<number>> {
  return servicePost<number>("WebCreateProductList", { name, sType });
}
